'use client';

import { Button, Divider, Label, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type {
  SeedingContentChannel,
  SeedingFulfillmentStatus,
  SeedingProposalItem,
} from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getPaths } from '../../common';
import { useDdayCountdown, useNavigate, useTrackImpression } from '../../common/hooks';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { isMusinsaService } from '../utils/campaignProposalMusinsaAnalytics';
import { buildCollaborateCampaignSnowplowData } from '../utils/seedingCollaborateAnalytics';
import {
  buildCollaborateProposalImpressionGtmAttr,
  trackCollaborateProposalActionClickMusinsa,
  trackCollaborateProposalCardClickMusinsa,
} from '../utils/seedingMusinsaAnalytics';

function getConfirmedLabel(fulfillmentStatus: SeedingFulfillmentStatus | null): {
  label: string;
  showRemaining: boolean;
} {
  switch (fulfillmentStatus) {
    case 'SHIPPING_READY':
      return { label: '상품 준비중', showRemaining: false };
    case 'SHIPPING':
      return { label: '배송중', showRemaining: false };
    case 'CANCELLED':
      return { label: '취소됨', showRemaining: false };
    default:
      return { label: '콘텐츠 등록까지', showRemaining: true };
  }
}

const CHANNEL_LABEL: Record<SeedingContentChannel, string> = {
  INSTAGRAM: '인스타그램',
  YOUTUBE: '유튜브',
  TWITTER: 'X(트위터)',
  TIKTOK: '틱톡',
  NAVER_BLOG: '네이버 블로그',
  THREADS: '스레드',
  SNAP: '스냅',
};

const CONTENT_TYPE_LABEL: Record<string, string> = {
  IMAGE: '이미지',
  SHORT_FORM: '숏폼 영상',
  LONG_FORM: '롱폼 영상',
  TEXT: '텍스트',
  BLOG_POST: '블로그 포스트',
};

interface Props {
  item: SeedingProposalItem;
  /** 리스트 내 0-based map index (이벤트 노출/클릭 순서용) */
  index?: number;
  onAccept?: (participationId: number) => void;
  onReject?: (participationId: number) => void;
  isActionPending?: boolean;
  /** 목록 최하단 카드면 하단 divider 미노출. (SID-4325) */
  isLast?: boolean;
}

export const SeedingProposalCard = ({
  item,
  index = 0,
  onAccept,
  onReject,
  isActionPending,
  isLast,
}: Props) => {
  const { service } = useDeviceInfo();
  const { navigate } = useNavigate();
  const paths = getPaths(service);
  // 상품 썸네일 비율 — 29CM=square(80x80) / 무신사=portrait(80x100). 전 지면 공통 정책. (SID-4424)
  const is29cm = service === SERVICE['29CM'];
  // curatorId — ProposalList 선례: react-query dedupe 로 중복 호출 없음.
  const { data: userInfo } = useQuery({ ...curatorQueries.profile.userInfo });
  const curatorId = userInfo?.snsTitle ?? '';

  // 협업하기(시딩) > 캠페인 목록 노출 (29CM Snowplow) — IMPLEMENTATION.md ③ #5
  const { ref: impressionRef } = useTrackImpression<HTMLButtonElement>({
    onImpression: () => {
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.IMPRESSION_CONTENT,
          data: buildCollaborateCampaignSnowplowData({
            campaignId: item.campaignId,
            stageStatus: item.searchStatus,
            index,
            curatorId,
          }),
        });
      }
    },
  });

  // 협업하기(시딩) > 제안 받은 카드 노출 (무신사 GA4) — ga4-collaboration-list.md no.1
  // getGtmAttr 결과의 className 은 버튼 기존 className 에 머지(직접 spread 시 className 덮어써 레이아웃 깨짐).
  const { className: musinsaImpressionClass = '', ...musinsaImpressionAttr } = isMusinsaService(
    service,
  )
    ? buildCollaborateProposalImpressionGtmAttr({
        campaignId: item.campaignId,
        searchStatus: item.searchStatus,
        brandId: item.brandId,
        curatorId,
      })
    : {};

  // 협업하기(시딩) > 캠페인 목록 클릭 (29CM Snowplow) — IMPLEMENTATION.md ③ #6
  const handleClick = () => {
    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_CONTENT,
        data: buildCollaborateCampaignSnowplowData({
          campaignId: item.campaignId,
          stageStatus: item.searchStatus,
          index,
          curatorId,
        }),
      });
    }
    // 협업하기(시딩) > 제안 받은 카드 클릭 (무신사 GA4 + Amplitude) — no.2 / no.12
    if (isMusinsaService(service)) {
      trackCollaborateProposalCardClickMusinsa({
        campaignId: item.campaignId,
        searchStatus: item.searchStatus,
        // campaign_title (BE 0625 제공)
        campaignTitle: item.title,
        brandId: item.brandId,
        curatorId,
      });
    }
    navigate(paths.campaignSeedingDetail({ campaignId: item.campaignId }));
  };

  const { label: countdownLabel, isUrgent, isExpired } = useDdayCountdown(item.timerEndsAt);
  const remaining = countdownLabel ? `${countdownLabel} 남음` : '';

  const showActions = item.searchStatus === 'PENDING';
  const confirmedSub =
    item.searchStatus === 'CONFIRMED' ? getConfirmedLabel(item.fulfillmentStatus) : null;
  const statusText = confirmedSub?.label ?? (item.searchStatus === 'PENDING' ? '수락 대기 중' : '');
  const showRemaining = item.searchStatus === 'PENDING' || (confirmedSub?.showRemaining ?? false);

  // 수락(CONFIRMED) 탭에서 '콘텐츠 등록까지' 타이머가 보이는 중 0 도달 시, 그 자리를
  // '콘텐츠 제출 기한 경과' 로 시각 전환(클라 전용·API 없음 — refetch 가 목록 정리). (QAT-39690 §1-2 / CMQA-7190)
  // isExpired 는 timerEndsAt 가 null 일 때도 true 이므로 타이머 존재 + showRemaining 으로 한정.
  const isContentDeadlineExpired =
    item.searchStatus === 'CONFIRMED' &&
    (confirmedSub?.showRemaining ?? false) &&
    item.timerEndsAt !== null &&
    isExpired;

  const isCompleted = item.searchStatus === 'COMPLETED';
  const isRejected = item.searchStatus === 'REJECTED';
  const isFinal = isCompleted || isRejected;
  const registered = isCompleted ? (item.registeredContents[0] ?? null) : null;
  const registeredLabel = registered
    ? `${dayjs(registered.registeredAt).format('YYYY.MM.DD')} ${CONTENT_TYPE_LABEL[registered.contentType] ?? registered.contentType} 등록`
    : null;

  return (
    <div className="px-[16px]! pt-[12px]! pb-[12px]!">
      <button
        ref={impressionRef}
        type="button"
        className={`w-full text-left cursor-pointer${musinsaImpressionClass ? ` ${musinsaImpressionClass}` : ''}`}
        onClick={handleClick}
        // 협업하기(시딩) > 제안 받은 카드 노출 (무신사 GA4 impression_content) — no.1
        {...musinsaImpressionAttr}
      >
        <div className="flex flex-col gap-[8px]">
          {!isFinal && (
            <div className="flex gap-[4px] items-center">
              {isContentDeadlineExpired ? (
                <Typography variant="body13pxSemi" colorScheme="gray-600">
                  콘텐츠 제출 기한 경과
                </Typography>
              ) : (
                <>
                  <Typography variant="body13pxSemi" colorScheme="gray-600">
                    {statusText}
                  </Typography>
                  {showRemaining && remaining && (
                    <Typography variant="body13pxReg" colorScheme={isUrgent ? 'red' : undefined}>
                      {remaining}
                    </Typography>
                  )}
                </>
              )}
            </div>
          )}
          <div className="flex">
            <div
              className={`w-[80px] ${is29cm ? 'h-[80px]' : 'h-[100px]'} bg-gray-200 rounded-[4px] shrink-0 overflow-hidden`}
            >
              {/** biome-ignore lint/performance/noImgElement: list row layout */}
              <img
                src={item.goodsImage}
                alt={item.goodsName}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 flex flex-col gap-[2px] pl-[12px]! justify-center">
              <Typography variant="body13pxSemi">{item.brandName}</Typography>
              <Typography variant="body14pxReg" lineClamp="two">
                {item.goodsSlots.length > 1
                  ? `${item.goodsName} 외 ${item.goodsSlots.length - 1}개`
                  : item.goodsName}
              </Typography>
              {registeredLabel && (
                <Typography variant="body13pxReg" colorScheme="gray-600">
                  {registeredLabel}
                </Typography>
              )}
              {!isFinal && item.snsChannels.length > 0 && (
                <div className="flex gap-[4px] mt-[2px]!">
                  {item.snsChannels.map((channel) => (
                    <Label key={channel} variant="grayOpacity">
                      <Typography size="11" colorScheme="gray-600">
                        {CHANNEL_LABEL[channel]}
                      </Typography>
                    </Label>
                  ))}
                </div>
              )}
              <Typography variant="body14pxSemi">{item.goodsPrice.toLocaleString()}원</Typography>
            </div>
          </div>
        </div>
      </button>
      {showActions && (
        <>
          <div className="flex gap-[4px] mt-[12px]!">
            <Button
              variant="secondary"
              size={36}
              className="flex-1"
              disabled={isActionPending}
              onClick={() => {
                // 협업하기(시딩) > 거절 버튼 클릭 (무신사 Amplitude) — amp-collaboration.md no.14
                if (isMusinsaService(service)) {
                  trackCollaborateProposalActionClickMusinsa({
                    campaignId: item.campaignId,
                    searchStatus: item.searchStatus,
                    action: '거절',
                    // campaign_title (BE 0625 제공)
                    campaignTitle: item.title,
                    curatorId,
                  });
                }
                onReject?.(item.participationId);
              }}
            >
              <Typography variant="body13pxMed">거절</Typography>
            </Button>
            <Button
              variant="secondary"
              size={36}
              className="flex-1"
              disabled={isActionPending}
              onClick={() => {
                // 협업하기(시딩) > 수락 버튼 클릭 (무신사 Amplitude) — amp-collaboration.md no.14
                if (isMusinsaService(service)) {
                  trackCollaborateProposalActionClickMusinsa({
                    campaignId: item.campaignId,
                    searchStatus: item.searchStatus,
                    action: '수락',
                    // campaign_title (BE 0625 제공)
                    campaignTitle: item.title,
                    curatorId,
                  });
                }
                onAccept?.(item.participationId);
              }}
            >
              <Typography variant="body13pxMed">수락</Typography>
            </Button>
          </div>
          {/* 목록 최하단 카드는 divider 미노출. (SID-4325) */}
          {!isLast && (
            <div className="pt-[24px]!">
              <Divider variant="line" />
            </div>
          )}
        </>
      )}
    </div>
  );
};
